import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { useHistory } from "react-router-dom";
import { DEFAULT_PER_PAGE } from "../../../../../util/util";
import { useDebounce } from "../../../../../util/hooks";
import {
  EMPTY_APPROVALS_LIST_FILTERS,
  toApprovalsListFilterParams,
} from "../ApprovalsList";
import type { ApprovalsListFiltersState } from "../ApprovalsList";

export const APPROVALS_LIST_RESTORE_FLAG = "approvals_list_restore";

/**
 * Shared pagination / search / filter state for product Approvals tab
 * and the storefront Approvals dashboard.
 */
export function useApprovalsListControls(
  initialFilters: ApprovalsListFiltersState = EMPTY_APPROVALS_LIST_FILTERS,
  storageKey?: string
) {
  const history = useHistory();
  const [saved] = useState(() => {
    try {
      const isReturning =
        history.action === "POP" ||
        sessionStorage.getItem(APPROVALS_LIST_RESTORE_FLAG) !== null;
      const raw =
        isReturning && storageKey && sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [perPage, setPerPage] = useState<number>(
    saved?.perPage ?? DEFAULT_PER_PAGE
  );
  const [offset, setOffset] = useState<number>(saved?.offset ?? 0);
  const [searchQuery, setSearchQuery] = useState<string>(
    saved?.searchQuery ?? ""
  );
  const [filters, setFilters] = useState<ApprovalsListFiltersState>(
    saved?.filters ?? initialFilters
  );
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  useEffect(() => {
    try {
      sessionStorage.removeItem(APPROVALS_LIST_RESTORE_FLAG);
    } catch {
      // Storage unavailable.
    }
  }, []);

  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ perPage, offset, searchQuery, filters })
      );
    } catch {
      // Storage unavailable — state just won't persist.
    }
  }, [storageKey, perPage, offset, searchQuery, filters]);

  const listFilterParams = useMemo(
    () => toApprovalsListFilterParams(filters, debouncedSearchQuery),
    [debouncedSearchQuery, filters]
  );

  return {
    perPage,
    offset,
    searchQuery,
    filters,
    listFilterParams,
    onPageChange: setOffset,
    onPerPageChange: (next: number) => {
      setPerPage(next);
      setOffset(0);
    },
    onSearchChange: (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setOffset(0);
    },
    onClearSearch: () => {
      setSearchQuery("");
      setOffset(0);
    },
    onFiltersChange: (next: ApprovalsListFiltersState) => {
      setFilters(next);
      setOffset(0);
    },
    setFilters,
  };
}
